import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Edit3, Archive, ArchiveRestore, Trash2, FileText, Loader2, Building2, User as UserIcon, Wallet, Layers,
  Mail, Phone, MapPin, Printer, ExternalLink, Send, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { money } from "../../lib/jobCalc";
import { CustomerStatementDoc } from "../../components/admin/CustomerStatementDoc";
import { CustomerPopiaCard } from "../../components/admin/CustomerPopiaCard";
import { BulkConsolidateModal } from "../../components/admin/BulkConsolidateModal";
import { StatementEmailModal } from "../../components/admin/StatementEmailModal";
import { openWhatsApp } from "../../lib/whatsapp";

const TERMS_LABEL = { cod: "C.O.D.", "30_days": "Nett 30 days", "60_days": "Nett 60 days" };

export const AdminCustomerDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStatement, setShowStatement] = useState(false);
  const [showConsolidate, setShowConsolidate] = useState(false);
  const [showStatementEmail, setShowStatementEmail] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await adminApi.customerHistory(id));
    } catch { toast.error("Could not load customer"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const generateStatement = async () => {
    setStatementLoading(true);
    try {
      const res = await adminApi.customerStatement(id, dateFrom || null, dateTo || null);
      setStatement(res);
      setShowStatement(true);
    } catch { toast.error("Could not generate statement"); }
    finally { setStatementLoading(false); }
  };

  const archive = async () => {
    if (!window.confirm(`Archive ${cust.code} — ${cust.name}? They stay in the register but hidden from pickers.`)) return;
    try {
      await adminApi.archiveCustomer(id);
      toast.success("Customer archived");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not archive"); }
  };

  const restore = async () => {
    try {
      await adminApi.restoreCustomer(id);
      toast.success("Customer restored");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not restore"); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this customer permanently? Only allowed if they have no jobs or credit notes.")) return;
    try {
      await adminApi.deleteCustomer(id);
      toast.success("Customer deleted");
      window.location.href = "/admin/customers";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const printStatement = () => {
    document.body.classList.add("printing-doc");
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("printing-doc"), 500);
    }, 50);
  };

  const cust = data?.customer;
  const jobs = data?.jobs || [];
  const totals = data?.totals || { billed: 0, paid: 0, outstanding: 0, job_count: 0 };
  const typeIcon = useMemo(() => {
    if (!cust) return UserIcon;
    if (cust.customer_type === "company") return Building2;
    if (cust.customer_type === "cod") return Wallet;
    return UserIcon;
  }, [cust]);
  const TypeIcon = typeIcon;

  if (loading) return <div className="p-10 text-center text-[#3f4b52]">Loading…</div>;
  if (!cust) return null;

  return (
    <div data-testid="admin-customer-detail">
      <div className="no-print">
        <Link to="/admin/customers" className="inline-flex items-center gap-1 text-xs font-display uppercase tracking-widest text-[#3f4b52] hover:text-[#f28a16]">
          <ArrowLeft size={14} /> Back to customers
        </Link>

        <div className="mt-2 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-[#f28a16] font-bold text-lg" data-testid="customer-code">{cust.code}</span>
              <h1 className="font-display font-black uppercase text-2xl sm:text-3xl text-[#111619]">{cust.name}</h1>
              <span className="inline-flex items-center gap-1.5 bg-[#edf2f4] text-[#3f4b52] text-xs font-display uppercase tracking-widest px-2 py-1 rounded-sm">
                <TypeIcon size={11} />
                {cust.customer_type === "company" ? "Company / Fleet" : cust.customer_type === "cod" ? "COD" : "Individual"}
              </span>
              {!cust.active && (
                <span className="bg-[#9aa6ad] text-white text-xs font-display uppercase tracking-widest px-2 py-1 rounded-sm">Inactive</span>
              )}
            </div>
            <div className="text-xs text-[#3f4b52] mt-1">
              {TERMS_LABEL[cust.account_terms] || cust.account_terms}
              {cust.vat_number && <> · VAT {cust.vat_number}</>}
              {cust.id_or_reg_number && <> · Reg {cust.id_or_reg_number}</>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowConsolidate(true)}
              className="bg-[#0ea5e9] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
              data-testid="customer-consolidate-btn"
            >
              <Layers size={14} /> Consolidate jobs
            </button>
            <Link
              to={`/admin/customers/${cust.id}/edit`}
              className="bg-[#20282c] text-white font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
              data-testid="customer-edit-btn"
            >
              <Edit3 size={14} /> Edit
            </Link>
            {cust.active ? (
              <button
                onClick={archive}
                className="border border-[#3f4b52] text-[#3f4b52] font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:bg-[#3f4b52]/10"
                data-testid="customer-archive-btn"
              >
                <Archive size={14} /> Archive
              </button>
            ) : (
              <button
                onClick={restore}
                className="border border-[#10b981] text-[#10b981] font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:bg-[#10b981]/10"
                data-testid="customer-restore-btn"
              >
                <ArchiveRestore size={14} /> Restore
              </button>
            )}
            {(totals.job_count === 0) && (
              <button
                onClick={remove}
                className="border border-[#dc2626] text-[#dc2626] font-display uppercase tracking-wider text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:bg-[#dc2626]/10"
                data-testid="customer-delete-btn"
              >
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
        </div>

        {/* Contact block */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          {cust.mobile && (
            <a href={`tel:${cust.mobile}`} className="bg-white border border-[#cfd7db] rounded-md p-3 flex items-center gap-3 hover:border-[#f28a16]">
              <Phone size={14} className="text-[#f28a16]" />
              <div>
                <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest">Mobile</div>
                <div className="text-sm text-[#111619]">{cust.mobile}</div>
              </div>
            </a>
          )}
          {cust.email && (
            <a href={`mailto:${cust.email}`} className="bg-white border border-[#cfd7db] rounded-md p-3 flex items-center gap-3 hover:border-[#f28a16]">
              <Mail size={14} className="text-[#f28a16]" />
              <div>
                <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest">Email</div>
                <div className="text-sm text-[#111619] break-all">{cust.email}</div>
              </div>
            </a>
          )}
          {cust.billing_address && (
            <div className="bg-white border border-[#cfd7db] rounded-md p-3 flex items-center gap-3">
              <MapPin size={14} className="text-[#f28a16] shrink-0" />
              <div>
                <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest">Billing address</div>
                <div className="text-sm text-[#111619]">{cust.billing_address}</div>
              </div>
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Total label="Jobs" value={totals.job_count} />
          <Total label="Billed" value={money(totals.billed)} color="#20282c" />
          <Total label="Paid" value={money(totals.paid)} color="#10b981" />
          <Total label="Outstanding" value={money(totals.outstanding)} color={totals.outstanding > 0 ? "#dc2626" : "#10b981"} />
        </div>

        {/* Statement generator */}
        <div className="mt-8 bg-white border border-[#cfd7db] rounded-md p-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Statement</div>
              <div className="font-display font-bold text-[#111619] mt-0.5">Generate an account statement</div>
              <p className="text-xs text-[#3f4b52] mt-1">Opening balance · invoices · payments · running balance · closing balance.</p>
            </div>
          </div>
          <div className="mt-4 flex items-end flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest mb-1">From</div>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10 px-3 bg-white border border-[#cfd7db] rounded-md text-sm" data-testid="stmt-from" />
            </div>
            <div>
              <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest mb-1">To</div>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 px-3 bg-white border border-[#cfd7db] rounded-md text-sm" data-testid="stmt-to" />
            </div>
            <button
              onClick={generateStatement}
              disabled={statementLoading}
              data-testid="stmt-generate"
              className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-60"
            >
              {statementLoading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              Generate statement
            </button>
            {showStatement && statement && (
              <>
                <button
                  onClick={() => adminApi.openDoc(adminApi.statementPdfUrl(id, dateFrom || null, dateTo || null))}
                  type="button"
                  data-testid="stmt-download-pdf"
                  className="bg-[#20282c] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:bg-black"
                >
                  <FileText size={14} /> Download PDF
                </button>
                <button
                  onClick={() => adminApi.openDoc(adminApi.statementXlsxUrl(id, dateFrom || null, dateTo || null), { action: "download" })}
                  type="button"
                  data-testid="stmt-download-xlsx"
                  className="bg-[#0f766e] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:bg-[#115e59]"
                >
                  <FileText size={14} /> Download Excel
                </button>
                <button
                  onClick={() => setShowStatementEmail(true)}
                  data-testid="stmt-email-btn"
                  className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110"
                >
                  <Send size={14} /> Email statement
                </button>
                <button
                  onClick={() => {
                    const closing = Number(statement?.closing_balance || 0);
                    const period = `${dateFrom || "opening"} → ${dateTo || "today"}`;
                    const msg =
                      `Hi ${(cust.name || "").split(" ")[0] || "there"}, here is your Diesel & Dust statement of account for ${period}.\n` +
                      `Closing balance: R ${closing.toFixed(2)}\n` +
                      (closing > 0
                        ? `Please arrange payment when possible — banking details are on the PDF.\n`
                        : `No outstanding balance — thanks for the prompt payment!\n`) +
                      `PDF: ${adminApi.statementPdfUrl(cust.id, dateFrom || null, dateTo || null)}`;
                    if (!cust.mobile) {
                      toast.warning("No mobile on this customer — WhatsApp will open without a number.");
                    }
                    openWhatsApp(cust.mobile, msg);
                  }}
                  data-testid="stmt-whatsapp-btn"
                  className="bg-[#25d366] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110"
                >
                  <MessageCircle size={14} /> WhatsApp
                </button>
                <button
                  onClick={printStatement}
                  data-testid="stmt-print"
                  className="border border-[#cfd7db] text-[#3f4b52] px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:bg-[#f5f6f6]"
                >
                  <Printer size={14} /> Print preview
                </button>
              </>
            )}
          </div>
        </div>

        {/* POPIA */}
        <div className="mt-6">
          <CustomerPopiaCard customer={cust} onErased={() => (window.location.href = "/admin/customers")} />
        </div>

        {/* Jobs history */}
        <div className="mt-8 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-[#cfd7db] flex items-center justify-between">
            <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Job history ({jobs.length})</div>
            <Link to={`/admin/jobs/new?customer_id=${cust.id}`} className="text-xs text-[#f28a16] hover:underline">+ New job for this customer</Link>
          </div>
          {jobs.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#3f4b52]">No jobs yet for this customer.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#edf2f4] text-[#3f4b52] uppercase tracking-widest text-xs">
                  <tr>
                    <th className="text-left px-4 py-2.5">#</th>
                    <th className="text-left px-4 py-2.5">Date</th>
                    <th className="text-left px-4 py-2.5">Vehicle</th>
                    <th className="text-left px-4 py-2.5">Type</th>
                    <th className="text-left px-4 py-2.5">Invoice</th>
                    <th className="text-right px-4 py-2.5">Billed</th>
                    <th className="text-right px-4 py-2.5">Paid</th>
                    <th className="text-right px-4 py-2.5">Outstanding</th>
                    <th className="text-right px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#eef2f4]">
                  {jobs.map((j) => (
                    <tr key={j.id} data-testid={`cust-job-${j.job_number}`}>
                      <td className="px-4 py-2.5 font-bold text-[#f28a16]">#{j.job_number}</td>
                      <td className="px-4 py-2.5 text-[#3f4b52]">{j.fitment_date || (j.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-2.5">
                        <div className="font-semibold text-[#111619]">{j.vehicle_make_model || "—"}</div>
                        <div className="text-xs text-[#3f4b52]">{j.registration || "—"}</div>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-[#3f4b52] uppercase">{j.payment_type}</td>
                      <td className="px-4 py-2.5 text-xs text-[#3f4b52]">
                        {j.invoice_number || "—"}
                        <div className="text-xs uppercase">{j.invoice_status}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right">{money(j.payable_by_customer)}</td>
                      <td className="px-4 py-2.5 text-right text-[#10b981]">{money(j.paid)}</td>
                      <td className={`px-4 py-2.5 text-right font-bold ${j.outstanding > 0 ? "text-[#dc2626]" : "text-[#10b981]"}`}>
                        {money(j.outstanding)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Link to={`/admin/jobs/${j.id}`} className="text-[#f28a16] text-xs hover:underline inline-flex items-center gap-1">
                          Open <ExternalLink size={10} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Printable statement */}
      {showStatement && statement && (
        <div className="mt-10 statement-print">
          <CustomerStatementDoc statement={statement} />
        </div>
      )}

      {showConsolidate && (
        <BulkConsolidateModal
          customer={cust}
          onClose={() => setShowConsolidate(false)}
          onCreated={(inv) => nav(`/admin/invoices/${inv.id}`)}
        />
      )}

      {showStatementEmail && statement && (
        <StatementEmailModal
          customer={cust}
          statement={statement}
          dateFrom={dateFrom || null}
          dateTo={dateTo || null}
          onClose={() => setShowStatementEmail(false)}
        />
      )}
    </div>
  );
};

const Total = ({ label, value, color = "#111619" }) => (
  <div className="bg-white border border-[#cfd7db] rounded-md p-4">
    <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest">{label}</div>
    <div className="font-display font-black text-2xl mt-0.5" style={{ color }}>{value}</div>
  </div>
);

export default AdminCustomerDetail;
